import { BodyMedium, RepeatIcon, theme, Tooltip, DeprecatedCloseIcon } from '@arnold/common';
import styled from '@emotion/styled/macro';
import { FC, ReactNode } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { SurveyGroupRespondentImportType, SurveyGroupWithRespondentsQuery, SurveyQuery } from '../../generated/hooks';
import { getPreferredContact } from '../../lib/helpers';
import { Table } from '../Table/Table';
import SurveyGroupChooseInstanceType from './SurveyGroupChooseInstanceType';

type ExtendedRespondent = NonNullable<SurveyQuery['survey']>['whitelistedRespondents'][0] & {
  type?: SurveyGroupRespondentImportType;
};
type TableProps = {
  respondents: ExtendedRespondent[];
  removeRespondent: (ids: string[]) => void;
  setRespondentRunType?: (id: string, type: SurveyGroupRespondentImportType) => void;
  isDisabled?: boolean;
  existingRespondents?: NonNullable<SurveyGroupWithRespondentsQuery['surveyGroup']>['surveyGroupRespondents'];
};

type ParticipantTableData = Pick<NonNullable<SurveyQuery['survey']>['whitelistedRespondents']['0'], 'id'> & {
  name?: ReactNode;
  email?: string;
  team?: string;
  remove?: ReactNode;
  render?: ReactNode;
};

const ExtraRowCell = styled.td`
  border: 1px solid ${theme.colors.borderMain.default};
  border-radius: ${theme.borderRadius.l};
  padding: ${theme.spacing.f};
`;
const RemoveButton = styled(Button)`
  display: flex !important;
  align-items: center;
  padding: 0;
  background: none;
  border: 0;
  margin: 0;

  &:hover,
  &:focus,
  &:active {
    background: none !important;
  }
  &:disabled {
    background: none !important;
    border: none !important;

    svg path {
      fill: ${theme.colors.actionPrimary.disabled};
    }
  }
`;

const RemoveLabelSpan = styled.span<Pick<TableProps, 'isDisabled'>>`
  margin-left: ${theme.spacing.e};
  font-size: ${theme.typography.body.medium!.button!.fontSize};
  font-weight: ${theme.typography.body.medium!.button!.fontWeight};
  color: ${({ isDisabled }) =>
    `${isDisabled ? theme.colors.actionPrimary.disabled : theme.colors.actionPrimary.default}`};
`;

const StyledTable = styled(Table)`
  border-collapse: separate;
`;

const StyledRepeatIcon = styled(RepeatIcon)`
  margin-left: ${theme.spacing.d};
  &:hover path {
    fill: ${theme.colors.emotionInformative.hover};
  }
`;
export const SurveyRespondentsTable: FC<TableProps> = ({
  respondents,
  removeRespondent,
  setRespondentRunType,
  isDisabled,
  existingRespondents,
}) => {
  const { t } = useTranslation('surveyForm');

  return (
    <>
      <StyledTable
        dataCypressTestFlag={'respondent-whitelist'}
        rowDataCypressTestFlag={'respondent-item'}
        thin={true}
        columns={[
          {
            key: 'name',
            title: t('report:firstname'),
            dataIndex: 'name',
          },
          {
            key: 'email',
            title: t('surveyContact'),
            dataIndex: 'email',
          },
          {
            key: 'team',
            title: t('survey:team'),
            dataIndex: 'team',
          },
          {
            key: 'remove',
            title: undefined,
            dataIndex: 'remove',
          },
        ]}
        wrapRow={(data, component) => {
          if (data.render) {
            return data.render;
          }
          return component;
        }}
        dataSources={respondents.reduce((arr, respondent) => {
          const alreadyParticipated = existingRespondents?.find(
            (r) => r.respondentId === respondent.id && r.isCompleted && (r.numberOfSurveys ?? 0) > 0,
          );
          arr.push({
            id: respondent.id,
            name: (
              <div data-cy="survey-participant-respondent-name">
                {`${respondent.firstname} ${respondent.surname}`}&nbsp;
                {alreadyParticipated && (
                  <Tooltip title={t('surveyGroupAddParticipants:alreadyParticipatedTooltip')}>
                    <StyledRepeatIcon />
                  </Tooltip>
                )}
              </div>
            ),
            email: getPreferredContact(respondent.preferredContactType, respondent.contacts || []).value,
            team: t('team', { team: respondent.team?.name || t('teams:withoutLeader') }),
            remove: (
              <RemoveButton
                data-cy={'btn-remove-respondent'}
                onClick={() => removeRespondent([respondent.id])}
                disabled={isDisabled}
              >
                <DeprecatedCloseIcon />
                <RemoveLabelSpan isDisabled={isDisabled}>{t('removeRespondent')}</RemoveLabelSpan>
              </RemoveButton>
            ),
          });
          const previousRun = existingRespondents
            ?.filter((r) => r.respondentId === respondent.id && !r.isCompleted && (r.numberOfSurveys ?? 0) > 0)
            .sort((a, b) => a.addedOn?.localeCompare(b.addedOn || ''))
            .pop();
          if (previousRun) {
            arr.push({
              id: `${respondent.id}-prev`,
              render: (
                <AlreadyAddedRow
                  respondent={respondent}
                  setRespondentRunType={setRespondentRunType}
                  numberOfSurveys={previousRun.numberOfSurveys!}
                />
              ),
            });
          }
          return arr;
        }, [] as ParticipantTableData[])}
        selected={undefined}
      />
    </>
  );
};

type AlreadyAddedRowProps = {
  respondent: ExtendedRespondent;
  setRespondentRunType?: (id: string, type: SurveyGroupRespondentImportType) => void;
  numberOfSurveys: number;
};

const AlreadyAddedRow: FC<AlreadyAddedRowProps> = ({ respondent, numberOfSurveys, setRespondentRunType }) => {
  const { t } = useTranslation('surveyGroupAddParticipants');
  return (
    <tr>
      <ExtraRowCell colSpan={4}>
        <BodyMedium>
          <strong>{`${respondent.firstname} ${respondent.surname}`}</strong>{' '}
          {t('alreadyAdded', { count: numberOfSurveys, postProcess: 'interval' })}
          <SurveyGroupChooseInstanceType respondent={respondent} setRespondentRunType={setRespondentRunType} />
        </BodyMedium>
      </ExtraRowCell>
    </tr>
  );
};
